import React, { useState, useRef, useEffect } from 'react';

const BOSS_NAMES = ['eno', 'indriti', 'indrit'];

export default function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! 👋 I'm the Rent355 assistant. How can I help you today? Ask me about our cars, prices, or how to book!",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isBoss = (text) => {
    const lower = text.toLowerCase();
    return BOSS_NAMES.some(name => lower.includes(name));
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          isBoss: isBoss(text),
        }),
      });

      const data = await response.json();
      const reply = data.reply || 'Sorry, I could not process that. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      if (!open) setUnread(u => u + 1);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please contact us on WhatsApp: +355682099501' }]);
    }

    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const quickReplies = ['💰 Prices', '🚗 Our cars', '📅 How to book', '📞 Contact us'];

  return (
    <>
      <style>{`
        @keyframes chat_in { from { opacity:0; transform:translateY(20px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes bubble_pulse { 0%,100% { box-shadow:0 8px 25px rgba(196,18,48,0.45); } 50% { box-shadow:0 8px 35px rgba(196,18,48,0.7); } }
        @keyframes dot_bounce { 0%,80%,100% { transform:translateY(0); opacity:0.4; } 40% { transform:translateY(-6px); opacity:1; } }
        .r355-chat-input:focus { outline:none; border-color:#C41230; background:#fff; }
        .r355-chat-send:hover:not(:disabled) { background:#A50F27; transform:translateY(-1px); }
        .r355-chat-send:disabled { opacity:0.5; cursor:not-allowed; }
        .r355-quick:hover { background:#FEE2E2; }
        .r355-msg-user { background:#C41230; color:#fff; border-radius:18px 18px 4px 18px; padding:10px 14px; max-width:82%; align-self:flex-end; font-size:14px; line-height:1.5; word-break:break-word; box-shadow:0 2px 8px rgba(196,18,48,0.25); }
        .r355-msg-ai { background:#F3F4F6; color:#111827; border-radius:18px 18px 18px 4px; padding:10px 14px; max-width:85%; align-self:flex-start; font-size:14px; line-height:1.5; word-break:break-word; }
      `}</style>

      {/* Chat Window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 20,
          width: Math.min(360, window.innerWidth - 40),
          height: 520,
          background: '#fff', borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', zIndex: 9999,
          animation: 'chat_in 0.25s ease', overflow: 'hidden',
          border: '1px solid #F0F0F0',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #C41230, #8B0D21)', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <div style={{ width: 42, height: 42, background: 'rgba(255,255,255,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🚗</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Rent355 Assistant</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <div style={{ width: 7, height: 7, background: '#4ADE80', borderRadius: '50%', flexShrink: 0 }}/>
                Online — usually replies instantly
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'r355-msg-user' : 'r355-msg-ai'}>
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="r355-msg-ai" style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '14px 16px' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 8, height: 8, background: '#C41230', borderRadius: '50%', animation: `dot_bounce 1.2s ease ${i * 0.2}s infinite` }}/>
                ))}
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Quick replies */}
          <div style={{ padding: '8px 14px 6px', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
            {quickReplies.map(q => (
              <button
                key={q}
                className="r355-quick"
                onClick={() => { setInput(q); inputRef.current?.focus(); }}
                style={{ background: '#FFF5F5', color: '#C41230', border: '1px solid #FECACA', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}>
                {q}
              </button>
            ))}
          </div>

          {/* Input */}
          <div style={{ padding: '8px 14px 16px', display: 'flex', gap: 8, flexShrink: 0, borderTop: '1px solid #F3F4F6' }}>
            <input
              ref={inputRef}
              className="r355-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type your message..."
              style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 12, fontSize: 14, fontFamily: 'inherit', color: '#111', background: '#FAFAFA', transition: 'all 0.15s' }}
            />
            <button
              className="r355-chat-send"
              onClick={send}
              disabled={loading || !input.trim()}
              style={{ background: '#C41230', color: '#fff', border: 'none', borderRadius: 12, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0 }}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
            </button>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', padding: '0 0 10px', fontSize: 11, color: '#D1D5DB' }}>
            Powered by Rent355 AI
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 20, right: 20, width: 58, height: 58,
          background: '#C41230', border: 'none', borderRadius: '50%',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, transition: 'transform 0.2s',
          animation: open ? 'none' : 'bubble_pulse 2.5s ease infinite',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.12)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {open ? (
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        ) : (
          <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
        )}
        {!open && unread > 0 && (
          <div style={{ position: 'absolute', top: -4, right: -4, background: '#25D366', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, border: '2px solid #fff' }}>
            {unread}
          </div>
        )}
      </button>
    </>
  );
}
